import { Controller } from '@hotwired/stimulus'
import { type PluralForms, plural } from '@/lib/plural'
import { popoverTrigger } from '@/lib/popover-placement'
import { fetchTimeZoneChoices, loadedTimeZoneChoices } from '@/lib/time-zone-choices'
import { groupByRegion, isListboxKey, moveHighlight, zoneQuestion } from '@/lib/time-zone-listbox'
import { type IndexedTimeZone, indexTimeZones, searchTimeZones } from '@/lib/time-zone-search'
import type { TimeZoneChoices, TimeZoneOption } from '@/types/serializers'

const OPTION = '[role="option"]'

let refocusAfterChange: string | null = null

export default class extends Controller<HTMLElement> {
  static override targets = [
    'panel',
    'warning',
    'question',
    'cancel',
    'confirm',
    'picker',
    'search',
    'list',
    'failure',
    'status',
    'form',
    'zone',
    'skeleton',
    'group',
    'option',
    'empty',
  ]

  static override values = {
    current: String,
    found: Object,
    empty: String,
    question: String,
    loading: String,
  }

  declare readonly panelTarget: HTMLElement
  declare readonly warningTarget: HTMLFieldSetElement
  declare readonly questionTarget: HTMLElement
  declare readonly cancelTarget: HTMLButtonElement
  declare readonly confirmTarget: HTMLButtonElement
  declare readonly pickerTarget: HTMLElement
  declare readonly searchTarget: HTMLInputElement
  declare readonly listTarget: HTMLElement
  declare readonly failureTarget: HTMLElement
  declare readonly statusTarget: HTMLElement
  declare readonly formTarget: HTMLFormElement
  declare readonly zoneTarget: HTMLInputElement
  declare readonly skeletonTarget: HTMLTemplateElement
  declare readonly groupTarget: HTMLTemplateElement
  declare readonly optionTarget: HTMLTemplateElement
  declare readonly emptyTarget: HTMLTemplateElement
  declare readonly currentValue: string
  declare readonly foundValue: PluralForms
  declare readonly emptyValue: string
  declare readonly questionValue: string
  declare readonly loadingValue: string

  private choices: TimeZoneChoices | null = null
  private index: IndexedTimeZone[] = []
  private options = new Map<string, HTMLElement>()
  private highlighted: string | null = null
  private confirming: TimeZoneOption | null = null
  private submitting = false

  override connect() {
    this.panelTarget.hidden = false
    if (refocusAfterChange !== this.element.id) return

    refocusAfterChange = null
    const trigger = popoverTrigger(this.element)
    const tray = trigger?.closest<HTMLElement>('[popover]')
    const reachable = tray && !tray.matches(':popover-open') ? popoverTrigger(tray) : trigger
    reachable?.focus({ preventScroll: true })
  }

  toggled(event: ToggleEvent) {
    if (event.newState === 'open') this.load()
    else if (!this.submitting) this.reset()
  }

  retry() {
    this.load()
    this.searchTarget.focus()
  }

  filter() {
    this.render()
  }

  navigate(event: KeyboardEvent) {
    if (isListboxKey(event.key)) {
      event.preventDefault()
      this.highlight(moveHighlight(this.optionValues(), this.highlighted, event.key), true)
    } else if (event.key === 'Enter') {
      event.preventDefault()
      if (this.highlighted) this.choose(this.highlighted)
    }
  }

  hover(event: PointerEvent) {
    const value = this.optionFrom(event)?.dataset.value
    if (value && value !== this.highlighted) this.highlight(value, false)
  }

  pick(event: MouseEvent) {
    const value = this.optionFrom(event)?.dataset.value
    if (value) this.choose(value)
  }

  escape(event: KeyboardEvent) {
    if (event.key !== 'Escape' || !this.confirming || this.submitting) return

    event.preventDefault()
    event.stopPropagation()
    this.cancel()
  }

  leave(event: FocusEvent) {
    const next = event.relatedTarget
    if (!(next instanceof Node) || this.element.contains(next)) return
    if (next === popoverTrigger(this.element)) return

    this.element.hidePopover()
  }

  cancel() {
    this.confirming = null
    this.warningTarget.hidden = true
    this.pickerTarget.classList.remove('tz-panel__picker--concealed')
    this.searchTarget.focus()
  }

  confirm() {
    if (this.confirming) this.submit(this.confirming.id)
  }

  submitted(event: CustomEvent<{ success: boolean }>) {
    if (event.detail.success) return

    this.submitting = false
    refocusAfterChange = null
    this.panelTarget.removeAttribute('aria-busy')
    this.confirmTarget.disabled = false
    this.cancelTarget.disabled = false
  }

  private load() {
    this.choices ??= loadedTimeZoneChoices(this.currentValue)
    if (this.choices) {
      this.indexChoices(this.choices)
      this.render()
      return
    }

    this.failureTarget.hidden = true
    this.listTarget.hidden = false
    this.listTarget.setAttribute('aria-busy', 'true')
    this.listTarget.replaceChildren(this.skeletonTarget.content.cloneNode(true))
    this.announce(this.loadingValue)

    fetchTimeZoneChoices(this.formTarget.getAttribute('action') ?? '', this.currentValue).then(
      (choices) => {
        this.choices = choices
        this.indexChoices(choices)
        this.render()
      },
      () => {
        this.listTarget.hidden = true
        this.failureTarget.hidden = false
        this.announce(this.failureTarget.querySelector('p')?.textContent ?? '')
      },
    )
  }

  private indexChoices(choices: TimeZoneChoices) {
    if (this.index.length === 0) this.index = indexTimeZones(choices.zones)
  }

  private render() {
    if (!this.choices) return

    const term = this.searchTarget.value.trim()
    const zones = searchTimeZones(this.index, term)
    this.listTarget.removeAttribute('aria-busy')
    this.listTarget.replaceChildren(
      ...(zones.length === 0
        ? [this.emptyNode(term)]
        : groupByRegion(zones).map(([region, members], i) => this.groupNode(region, members, i))),
    )
    this.highlight(term === '' ? null : (zones[0]?.id ?? null), true)
    this.announce(term === '' ? '' : plural(zones.length, this.foundValue))
  }

  private groupNode(region: string, zones: TimeZoneOption[], position: number): Node {
    const fragment = this.groupTarget.content.cloneNode(true) as DocumentFragment
    const group = fragment.firstElementChild as HTMLElement
    const label = group.firstElementChild as HTMLElement
    label.id = `${this.element.id}-group-${position}`
    label.textContent = region
    group.setAttribute('aria-labelledby', label.id)
    group.append(...zones.map((zone) => this.optionNode(zone)))
    return fragment
  }

  private optionNode(zone: TimeZoneOption): HTMLElement {
    const cached = this.options.get(zone.id)
    if (cached) return cached

    const option = (this.optionTarget.content.cloneNode(true) as DocumentFragment)
      .firstElementChild as HTMLElement
    option.id = `${this.element.id}-option-${zone.id}`
    option.dataset.value = zone.id
    option.setAttribute('aria-selected', String(zone.id === this.currentValue))
    const [text, detail] = option.querySelectorAll('span')
    if (text) text.textContent = zone.label
    if (detail) detail.textContent = zone.offset_label
    this.options.set(zone.id, option)
    return option
  }

  private emptyNode(term: string): Node {
    const fragment = this.emptyTarget.content.cloneNode(true) as DocumentFragment
    const title = fragment.querySelector('p')
    if (title) title.textContent = this.emptyValue.replace('%{term}', term)
    return fragment
  }

  private highlight(value: string | null, reveal: boolean) {
    this.highlighted = value
    let active: HTMLElement | null = null
    for (const option of this.listTarget.querySelectorAll<HTMLElement>(OPTION)) {
      if (option.dataset.value === value) {
        option.dataset.highlighted = ''
        active = option
      } else {
        delete option.dataset.highlighted
      }
    }

    for (const element of [this.searchTarget, this.listTarget]) {
      if (active) element.setAttribute('aria-activedescendant', active.id)
      else element.removeAttribute('aria-activedescendant')
    }
    if (reveal) active?.scrollIntoView({ block: 'nearest' })
  }

  private choose(id: string) {
    if (this.submitting) return
    if (id === this.currentValue) {
      this.element.hidePopover()
      return
    }

    const target = this.choices?.zones.find((zone) => zone.id === id)
    if (!target) return
    if (this.choices?.moves_schedule) {
      this.askToConfirm(target)
      return
    }
    this.submit(id)
  }

  private askToConfirm(target: TimeZoneOption) {
    this.confirming = target
    this.questionTarget.textContent = zoneQuestion(this.questionValue, target)
    this.warningTarget.hidden = false
    this.pickerTarget.classList.add('tz-panel__picker--concealed')
    requestAnimationFrame(() => this.questionTarget.focus())
  }

  private submit(id: string) {
    this.submitting = true
    refocusAfterChange = this.element.id
    this.panelTarget.setAttribute('aria-busy', 'true')
    this.confirmTarget.disabled = true
    this.cancelTarget.disabled = true
    this.zoneTarget.value = id
    this.formTarget.requestSubmit()
  }

  private reset() {
    this.confirming = null
    this.highlighted = null
    this.warningTarget.hidden = true
    this.pickerTarget.classList.remove('tz-panel__picker--concealed')
    this.searchTarget.value = ''
    this.announce('')
  }

  private announce(text: string) {
    this.statusTarget.textContent = text
  }

  private optionValues(): string[] {
    return [...this.listTarget.querySelectorAll<HTMLElement>(OPTION)].flatMap((option) =>
      option.dataset.value ? [option.dataset.value] : [],
    )
  }

  private optionFrom(event: Event): HTMLElement | null {
    return event.target instanceof Element ? event.target.closest<HTMLElement>(OPTION) : null
  }
}
